import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router'
import { connect } from 'react-redux'

import withApiError, {
  ERROR_TYPE_LMS,
  ERROR_TYPE_PROFILE,
  SHOW_ERROR_IN_MODAL,
} from 'layers/errorHandling/apiError'
import { productMap } from 'utils/shapes'
import { operations as productOperations } from 'store/productManager'
import { isMiddleSchoolUrl } from 'utils/productHelpers'
import { Loading, ErrorMessage, NotFound } from 'secondstep-components'
import PagesContainer from 'containers/Pages'
import PagesV2Container from 'containers/PagesV2'
import Sela from 'pages/Sela'
import Training from 'pages/Training'
import Lessons from 'pages/Lessons'
import LessonsV2 from 'pages/LessonsV2'

import LessonsV3 from 'pages/LessonsV3'
import K8Teach from 'pages/K8Teach'
import TieredInterventionsUnits from 'pages/TieredInterventionsUnits'

import HighSchoolTrainingLanding from 'pages/HighSchoolTrainingLanding'
import IndividualResource from 'pages/IndividualResource'
import ResourcesLanding from 'pages/ResourcesLanding'
// TODO: fix import when the High School + Middle School flags are removed:
import ResourceContent from 'pages/ResourcesContent/flag.on'
import LearnErrorBoundary from 'components/LearnErrorBoundary'
import { isFlagOn } from 'utils/featureFlags'

import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'

import {
  getStaticRoutes,
  getPagesV2StaticRoutesHighSchool,
  getPagesV2StaticRoutesElementary,
  getPagesV2StaticRoutesMiddleSchool,
} from './data'
import { ProductWrapper } from './component.styles'
import { HIGH_SCHOOL_INDIVIDUAL_RESOURCE_PATH } from 'utils/highschoolHelpers'
import {
  ELEMENTARY_INDIVIDUAL_RESOURCE_PATH,
  MIDDLE_SCHOOL_INDIVIDUAL_RESOURCE_PATH,
} from 'utils/programHelpers'

export class ProductContainer extends Component {
  static propTypes = {
    getProductMap: PropTypes.func.isRequired,
    lmsError: PropTypes.object,
    match: PropTypes.object.isRequired,
    productMap,
    resetContentNodes: PropTypes.func,
    theme: PropTypes.object,
  }

  constructor(props) {
    super(props)
    this.state = {
      errorStatus: null,
      errorDetails: null,
    }
  }

  componentDidMount() {
    const { getProductMap, match } = this.props
    const map = getProductMap(match.params.productName)

    if (!map) {
      const error = {
        errorStatus: 404,
        errorDetails:
          'Requested program was not found. Please check the url and try again.',
      }
      this.setState(error)
    }
  }

  render() {
    const { props, state } = this
    const { errorStatus, errorDetails } = state || {}
    const { match, productMap, location } = props || {}
    const { path } = match || {}

    if (errorStatus) {
      return (
        <ErrorMessage
          additionalDetails={errorDetails}
          statusCode={errorStatus}
        />
      )
    }

    if (!productMap) {
      return <Loading loadingText="Loading program data..." />
    }

    const isSela =
      match?.params.productName === 'sel-for-adults' ||
      'sel-for-adults-field-test'

    // TODO LEARN-23339 Update these to work with react version of feature flags

    const isHighSchoolJuneRelease = isFlagOn(['high-school-june-release'])
    const isHsExtensionActivities = isFlagOn([
      'feature_learn-18453-hs-student-extension-activities',
    ])

    // TODO REMOVE MIDDLE SCHOOL JUNE RELEASE: middle school june release allows us to keep this flag checker on. So DO NOT turn off or remve this flag
    // until we have a replacement for it. Used in multiple places so update all of them at once
    const isMsJuneRelease =
      isFlagOn(['middle-school-june-release']) && isMiddleSchoolUrl()

    const isMsSiteSelection =
      isFlagOn(['feature_LEARN-20108-ms-site-selection']) &&
      location.pathname.includes('/product/middle-school/')
    const isK5SiteSelection =
      isFlagOn(['feature_LEARN-20164-k5-site-selection']) &&
      location.pathname.includes('/product/elementary/')

    const shouldShowTI = isFlagOn([
      'feature_LEARN-23137_add-tiered-interventions-to-learn',
    ])

    return (
      <LearnErrorBoundary>
        <ProductWrapper
          isLessonsLanding={location?.pathname?.endsWith('/lessons')}
          isSelaPath={isSela}
        >
          <Switch>
            {shouldShowTI && (
              <Route
                component={TieredInterventionsUnits}
                exact
                path={`/product/:program/lessons/tier-2/:verb/:gradeBand/:focusArea`}
              />
            )}

            <Route
              component={IndividualResource}
              path={[
                `${HIGH_SCHOOL_INDIVIDUAL_RESOURCE_PATH}/:slug`,
                `${ELEMENTARY_INDIVIDUAL_RESOURCE_PATH}/:slug`,
                `${MIDDLE_SCHOOL_INDIVIDUAL_RESOURCE_PATH}/:slug`,
              ]}
            />
            {/* this route is only available in Program Flex: */}
            {isHighSchoolJuneRelease && (
              <Route
                component={HighSchoolTrainingLanding}
                // only match the high school landing training page, not its children:
                exact
                path="/product/high-school/training"
              />
            )}
            <Route
              component={ResourceContent}
              path="/product/high-school/pathways/:pathway_name/overview"
            />
            <Route
              component={ResourcesLanding}
              exact
              path="/product/high-school/program-supports"
            />
            {/* these routes will use PagesV2Container. order here matters so please make sure it's before 'PagesContainer' */}
            {isHsExtensionActivities && (
              <Route
                component={PagesV2Container}
                path={getPagesV2StaticRoutesHighSchool()}
              />
            )}
            {isMsJuneRelease && (
              <Route
                component={PagesV2Container}
                path={getPagesV2StaticRoutesMiddleSchool()}
              />
            )}
            {isK5SiteSelection && (
              <Route
                component={withSiteContext(PagesV2Container)}
                path={getPagesV2StaticRoutesElementary()}
              />
            )}
            {isMsSiteSelection && (
              <Route
                component={withSiteContext(PagesV2Container)}
                path={getPagesV2StaticRoutesMiddleSchool()}
              />
            )}
            {/* TODO LEARN-23339: remove this once we have the new lessons v3 working */}
            <Route component={PagesContainer} path={getStaticRoutes(path)} />
            <Route component={Sela} path={`${path}/modules`} />

            {isMsSiteSelection && (
              <Route
                component={K8Teach}
                exact
                path={[
                  `/product/middle-school/lessons/:verb/:course`,
                  `/product/middle-school/lessons/:verb/:course/:unit`,
                ]}
              />
            )}
            {isK5SiteSelection && (
              <Route
                component={K8Teach}
                exact
                path={[
                  `/product/elementary/lessons/:verb/:course`,
                  `/product/elementary/lessons/:verb/:course/:unit`,
                ]}
              />
            )}
            {isMsSiteSelection && (
              <Route
                component={LessonsV3}
                path={[`/product/middle-school/lessons`]}
              />
            )}
            {isK5SiteSelection && (
              <Route
                component={LessonsV3}
                path={[`/product/elementary/lessons`]}
              />
            )}
            {/* TODO LEARN-23339: remove this once we have the new lessons v3 working */}
            <Route
              component={LessonsV2}
              path={`/product/middle-school/lessons`}
            />
            <Route component={Lessons} path={`${path}/lessons`} />
            <Route component={Training} path={`${path}/training`} />
            <Redirect
              exact
              from={path}
              to={`${path}/${productMap.defaultPage}`}
            />
            <Route component={NotFound} />
          </Switch>
        </ProductWrapper>
      </LearnErrorBoundary>
    )
  }
}

const mapStateToProps = ({ productManager }) => {
  return {
    productMap: productManager.productMap,
  }
}

const mapDispatchToProps = {
  getProductMap: productOperations.getProductMap,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  withApiError(
    ProductContainer,
    [ERROR_TYPE_PROFILE, ERROR_TYPE_LMS],
    [SHOW_ERROR_IN_MODAL],
  ),
)
